import ValidatableFormTabs from "../ValidatableFormTabs/ValidatableFormTabs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    selectActualVersion,
    selectCurrentApplicationTemplateVersion,
    selectReleasedApplications
} from "../../store/workshop/selectors"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { Formik } from "formik"
import ApplicationForm from "./ApplicationForm"
import { buildCreateOrUpdateRequest, buildFormValidationSchema, buildInitialValues } from "./helpers/builders"
import { FormikValues } from "formik/dist/types"
import {
    useLazySearchApplicationTemplateVersionQuery,
    useReleaseApplicationMutation,
    useUpdateApplicationInstanceMutation
} from "../../api/controllers/workshop"
import { selectCurrentProjectId } from "../../store/projects/selectors"
import { ApplicationTemplateEntityType, TemplateVersionStatus } from "../../models/workshop/common"
import { RootState } from "../../store/store"
import { ApplicationInstance } from "../../models/workshop/instance"
import { fillSettings } from "./helpers/fillSettings"
import { safeJsonParse } from "../../utility/common/safeJsonParse"
import { mockedSlot } from "../../utility/tests/mockedObjects"
import { workshopActions } from "../../store/workshop/slice"
import { getDefaultFields } from "./helpers/getDefaultFields"
import { useSlots } from "../../hooks/useSlots"

interface ApplicationFormikProps {
    submitCallback?: (application?: ApplicationInstance) => void
    title?: string
    icon?: string
    releasedApplicationId?: string
    templateId?: string
    inScenario?: boolean
}

const tNamespace = "workshop:form."

const ApplicationFormik = (props: ApplicationFormikProps) => {
    const { submitCallback, releasedApplicationId, title, icon, templateId, inScenario } = props

    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { slots: projectSlots, refetchSlots } = useSlots()

    const projectId = useAppSelector(selectCurrentProjectId)

    const releasedApplications = useAppSelector(selectReleasedApplications)
    const releasedApplication = useMemo(
        () => releasedApplications.find(app => app.Id === releasedApplicationId),
        [releasedApplications, releasedApplicationId]
    )

    const [isApplicationUpdating, setIsApplicationUpdating] = useState(false)

    const templateCurrentVersion = useAppSelector(selectCurrentApplicationTemplateVersion)
    const templateActualVersion = useAppSelector((state: RootState) =>
        selectActualVersion(state, releasedApplication?.TemplateId)
    )

    const settingsFields = (releasedApplication || templateCurrentVersion)?.Settings.Fields ?? []
    const updatedSettingsFields = templateActualVersion ? templateActualVersion?.Settings.Fields : []

    let isUpdateAvailable = false
    if (releasedApplication && templateActualVersion && templateActualVersion.Id) {
        isUpdateAvailable = releasedApplication.VersionId !== templateActualVersion.Id
    }

    const channelEntity = (templateActualVersion || templateCurrentVersion)?.EntitiesTemplates.find(
        entity => entity.EntityType === ApplicationTemplateEntityType.CHANNEL
    )

    const [releaseApplication, { isLoading: isCreatingApplication }] = useReleaseApplicationMutation()
    const [updateApplication, { isLoading: isUpdatingApplication }] = useUpdateApplicationInstanceMutation()
    const [getPublishedVersion] = useLazySearchApplicationTemplateVersionQuery()

    const isCreatingOrUpdating = !releasedApplication || isApplicationUpdating

    useEffect(() => {
        return () => {
            dispatch(workshopActions.setCurrentApplicationTemplateVersion(undefined))
            dispatch(workshopActions.setCurrentApplicationTemplateVersionId(""))
        }
    }, [])

    useEffect(() => {
        if (!templateId) {
            return
        }

        getPublishedVersion({
            TemplateIds: [templateId],
            Status: TemplateVersionStatus.PUBLISH,
            IsDeleted: false,
            IsReleasingApplication: true
        })
    }, [templateId])

    const tabEntries = [{ key: "general", value: t(`${tNamespace}general`) }]
    const defaultFields = getDefaultFields(t, releasedApplication?.Title, isCreatingOrUpdating, channelEntity)

    const fullSettings = [...defaultFields, ...(isApplicationUpdating ? updatedSettingsFields : settingsFields)]

    const slots = templateCurrentVersion?.EntitiesTemplates.filter(
        entity => entity.EntityType === ApplicationTemplateEntityType.SLOT
    ).map(entity => safeJsonParse(entity.Template, mockedSlot))

    const handleApplicationUpdate = () => {
        setIsApplicationUpdating(true)
    }

    const handleReleaseApplication = async (values: FormikValues) => {
        if (!projectId) {
            submitCallback?.()
            return
        }

        if (!releasedApplication && templateCurrentVersion) {
            const requestBody = buildCreateOrUpdateRequest(
                values,
                templateCurrentVersion,
                fullSettings,
                projectId,
                templateCurrentVersion.Id
            )

            const application = await releaseApplication(requestBody).unwrap()
            refetchSlots()
            submitCallback?.(application)
        } else if (releasedApplication) {
            let requestBody

            if (isApplicationUpdating) {
                requestBody = buildCreateOrUpdateRequest(
                    values,
                    templateActualVersion,
                    fullSettings,
                    projectId,
                    templateActualVersion.Id,
                    templateActualVersion.Meta
                )
            } else {
                requestBody = buildCreateOrUpdateRequest(
                    values,
                    releasedApplication,
                    fullSettings,
                    projectId,
                    releasedApplication.VersionId
                )
            }

            const application = await updateApplication({
                ...requestBody,
                ApplicationId: releasedApplication.Id || ""
            }).unwrap()
            refetchSlots()
            submitCallback?.(application)
        }
    }

    const filledSettings = useMemo(
        () => fillSettings(fullSettings, releasedApplication?.Settings.Fields ?? []),
        [releasedApplication, fullSettings]
    )
    const initialValues = buildInitialValues(filledSettings)
    const validationSchema = useMemo(() => buildFormValidationSchema(fullSettings), [fullSettings])

    return (
        <ValidatableFormTabs id="application-settings-tabs" alwaysReload={false} entries={tabEntries}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleReleaseApplication}
                enableReinitialize
            >
                <ApplicationForm
                    slotsInApplication={slots}
                    title={title}
                    icon={icon}
                    fields={fullSettings}
                    isUpdateAvailable={isUpdateAvailable && !isApplicationUpdating}
                    isLoading={isCreatingApplication || isUpdatingApplication}
                    handleUpdate={handleApplicationUpdate}
                    inScenario={inScenario}
                    slots={projectSlots}
                />
            </Formik>
        </ValidatableFormTabs>
    )
}

export default ApplicationFormik
